import { useState } from "react"
import { Modal, Platform, Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { Show, useObservable } from "@legendapp/state/react"
import { useDebounce } from "use-debounce"

import { ar } from "@src/i18n"
import { cn, formatDateForDisplay, priceFromDBFormat, sharedStyles, trpc } from "@src/utils"
import { ActivityIndicator, Text, TextInput, MCIcon } from "./core"
import { Header } from "./Header"
import { List } from "./list"

type Props = {
  id?: number
  onChange: (id?: number) => void
  className?: string
}

export function ReceiptPicker({ id, className, onChange }: Props) {
  const receiptsModal$ = useObservable(false)
  const receipt = trpc.receipts.get.useQuery({ id: id! }, { enabled: !!id })

  return (
    <>
      <Pressable className={cn("h-11 bg-gray-50 border border-gray-300 rounded px-4 justify-center active:opacity-50", className)} onPress={() => receiptsModal$.set(true)}>
        <Text className={`text-left ${receipt.data?.number ? "text-gray-900" : "text-gray-500"}`}>
          {receipt.data?.number ? `Receipt #${receipt.data.number}` : ar.selectReceipt}
        </Text>
      </Pressable>

      <Show if={receiptsModal$}>
        <ReceiptPickModal hide={() => receiptsModal$.set(false)} onSelectionChange={onChange} />
      </Show>
    </>
  )
}

function ReceiptPickModal({ onSelectionChange, hide }: { onSelectionChange: (id: number) => void; hide: () => void }) {
  const [hash, setHash] = useState("")
  const [debouncedHash] = useDebounce(hash, 500)

  const receipts = trpc.receipts.lookup.useInfiniteQuery({ hash: debouncedHash || undefined, limit: 20 }, { getNextPageParam: (lastPage) => lastPage.nextCursor })

  const items = receipts.data?.pages.flatMap((page) => page.items) ?? []

  const ListEmptyComponent = receipts.isLoading ? (
    <ActivityIndicator className="pt-4" /> //
  ) : (
    <View className="pt-4">
      <Text className="text-gray-500 text-center">{ar.noReceipts}</Text>
    </View>
  )

  return (
    <Modal animationType="slide" presentationStyle="pageSheet" onRequestClose={hide}>
      <Header
        title={ar.receiptLookup}
        withoutInsets={Platform.OS === "ios"}
        canGoBack={false}
        right={
          <Pressable onPress={hide} className="h-full px-2 justify-center">
            <MCIcon name="close" size={25} />
          </Pressable>
        }
      />
      <TextInput className="w-full mt-2 max-w-content-wide mx-auto h-11" placeholder={ar.searchByReceiptHash} value={hash} onChangeText={setHash} />
      <List
        className="flex-1"
        contentContainerStyle={sharedStyles.contentContainer}
        data={items}
        keyExtractor={(item) => item.hash}
        onEndReached={() => (receipts.hasNextPage ? receipts.fetchNextPage() : undefined)}
        onRefresh={receipts.refetch}
        onEndReachedThreshold={0.8}
        ListEmptyComponent={ListEmptyComponent}
        renderItem={({ item: receipt }) => {
          return (
            <Pressable
              className="bg-white py-3 px-4 mb-1 mx-1 rounded border border-gray-200 active:opacity-70"
              onPress={() => {
                onSelectionChange(receipt.id)
                hide()
              }}
            >
              <View className="flex-row items-center justify-between">
                <View className="flex-row items-baseline gap-2">
                  <Text className="font-bold text-gray-900">#{receipt.number}</Text>
                  <Text className="text-xs text-gray-400">ID: {receipt.id}</Text>
                </View>
                <Text className="text-sm text-gray-500">{formatDateForDisplay(receipt.time)}</Text>
              </View>

              <View className="flex-row items-center justify-between mt-1">
                <Text className="text-sm text-gray-600">
                  {ar.items}: {receipt.itemsCount}
                </Text>
                <Text className="text-sm font-semibold text-gray-900">
                  {priceFromDBFormat(receipt.amount)} {ar.egp}
                </Text>
              </View>

              <Text className="text-xs text-gray-400 mt-1" numberOfLines={1}>
                {receipt.hash}
              </Text>
            </Pressable>
          )
        }}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />
    </Modal>
  )
}
